/**
 * Email verification handler
 */
import { pushFunnelEvent } from '@utils/dataLayer';
import BaseHandler from './BaseHandler';
import DOMUtils from '../utils/DOMUtils';
import FormValidator from '../utils/FormValidator';
import { FORM_SELECTORS, FIELD_SELECTORS, MESSAGE_SELECTORS } from '../constants';

class EmailVerificationHandler extends BaseHandler {
  constructor(form, api, stepNavigator, messages) {
    super(form, api, stepNavigator, messages);
    this.validator = new FormValidator(messages);
  }

  /**
   * Validate and show verification form
   */
  async validateAndShowVerifyEmailForm(key, login) {
    const showError = (message) => {
      // Set email in verify-email form if login is an email
      if (login && FormValidator.validateEmail(login)) {
        const verifyEmailInput = this.form.querySelector(FIELD_SELECTORS.verifyEmailDisplay);
        if (verifyEmailInput) {
          verifyEmailInput.value = login;
        }
      }

      // Go to verify-email step
      this.stepNavigator.goToStep('verify-email');

      // Show error message on verify-email step
      const verifyEmailMessage = this.form.querySelector(MESSAGE_SELECTORS.verifyEmailMessage);
      if (verifyEmailMessage) {
        DOMUtils.showMessage(verifyEmailMessage, message, 'error');
      }
    };

    try {
      const { response, data } = await this.api.validateVerificationKey(key, login);

      if (response.ok && data.valid) {
        // Automatically verify email
        this.handleVerifyEmail(key, login);
      } else {
        showError(this.getErrorMessage(data));
      }
    } catch (error) {
      showError(this.getErrorMessage(error));
    }
  }

  /**
   * Handle email verification
   */
  async handleVerifyEmail(key, login) {
    // Go to verify-email step to show messages
    this.stepNavigator.goToStep('verify-email');

    const verifyEmailMessage = this.form.querySelector(MESSAGE_SELECTORS.verifyEmailMessage);

    try {
      const { response, data } = await this.api.verifyEmail(key, login);

      if (response.ok) {
        const message = this.getErrorMessage(data, 'message_email_verified');
        DOMUtils.showMessage(verifyEmailMessage, message, 'success');

        // Track Sign Up step 4: Success for Email
        pushFunnelEvent({
          section: 'Sign Up',
          step: 4,
          label: 'Sign Up - Success',
          method: 'Email',
        });

        // Redirect after successful verification
        const storedRedirect = this.getRedirectUrl();
        if (storedRedirect) {
          localStorage.removeItem('azds_sso_redirect');
        }
        const targetUrl = storedRedirect || data.redirect || window.location.href;
        window.location.href = targetUrl;
      } else {
        const message = this.getErrorMessage(data, 'error_email_verification');
        DOMUtils.showMessage(verifyEmailMessage, message, 'error');
      }
    } catch (error) {
      const message = this.getErrorMessage(error, 'error_connection');
      DOMUtils.showMessage(verifyEmailMessage, message, 'error');
    }
  }

  /**
   * Resend verification link
   */
  async handleResendVerification() {
    const emailInput = this.form.querySelector(FIELD_SELECTORS.verifyEmailDisplay);
    const email = emailInput.value.trim();

    if (!FormValidator.validateEmail(email)) {
      return;
    }

    const verifyEmailMessage = this.form.querySelector(MESSAGE_SELECTORS.verifyEmailMessage);

    // For resend, we use updateMessage instead of showMessage
    const resendForm = this.form.querySelector(FORM_SELECTORS.resendVerification);
    DOMUtils.setLoading(resendForm, true, this.form);

    try {
      const currentUrl = DOMUtils.getCurrentPageUrl();
      const { response, data } = await this.api.resendVerification(email, currentUrl);

      if (response.ok) {
        const message = this.getErrorMessage(data, 'message_verification_sent');
        DOMUtils.updateMessage(verifyEmailMessage, message, 'success');
      } else {
        const message = this.getErrorMessage(data, 'error_resend_verification');
        DOMUtils.updateMessage(verifyEmailMessage, message, 'error');
      }
    } catch (error) {
      const message = this.getErrorMessage(error, 'error_connection');
      DOMUtils.updateMessage(verifyEmailMessage, message, 'error');
    } finally {
      DOMUtils.setLoading(resendForm, false, this.form);
    }
  }
}

export default EmailVerificationHandler;
